import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Save, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inputCls, itDate } from "@/components/kit";
import { Stars } from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";

function Dish({ d, set, i }) {
  return (
    <div className={`rounded-xl border p-4 transition-colors ${d.scelto === true ? "border-[#34B3B0] bg-[#F0FDFA]" : d.scelto === false ? "border-[#FECACA] bg-[#FEF2F2] opacity-70" : "border-[#D4E6EC] bg-white"}`} data-testid={`tasting-dish-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="font-serif-it text-xl">{d.piatto}</div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => set({ scelto: d.scelto === true ? null : true })} className={`h-11 w-11 rounded-full grid place-items-center ${d.scelto === true ? "bg-[#34B3B0] text-white" : "border border-[#D4E6EC]"}`} data-testid={`tasting-yes-${i}`} aria-label="Scelto"><Check size={18} /></button>
          <button onClick={() => set({ scelto: d.scelto === false ? null : false })} className={`h-11 w-11 rounded-full grid place-items-center ${d.scelto === false ? "bg-[#E07A5F] text-white" : "border border-[#D4E6EC]"}`} data-testid={`tasting-no-${i}`} aria-label="Scartato"><X size={18} /></button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-2"><Stars value={d.voto} onChange={(n) => set({ voto: n })} testId={`tasting-stars-${i}`} /><input className={`${inputCls} flex-1 min-w-[160px]`} placeholder="Nota (più sale, porzione…)" value={d.nota || ""} onChange={(e) => set({ nota: e.target.value })} data-testid={`tasting-note-${i}`} /></div>
    </div>
  );
}

export default function Tasting() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get(`/contracts/${id}/tasting`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!t) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const set = (i) => (p) => setT({ ...t, dishes: t.dishes.map((d, j) => (j === i ? { ...d, ...p } : d)) });
  const save = async () => {
    try {
      await api.put(`/contracts/${id}/tasting`, { dishes: t.dishes, note: t.note });
      toast.success("Degustazione salvata");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const apply = async () => {
    if (!(await save())) return;
    try {
      const r = await api.post(`/contracts/${id}/tasting/apply`);
      toast.success(`Menù del contratto aggiornato (${r.data.updated} portate)`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const secs = [...new Set(t.dishes.map((d) => d.sezione))];
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="tasting-page">
      <button onClick={() => nav(`/contratti/${id}`)} className="text-sm text-[#47606B] inline-flex items-center gap-1"><ArrowLeft size={14} /> Torna al contratto {t.number}</button>
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#34B3B0]">Prova menù • {itDate(t.evento_data)} • {t.sede}</div>
        <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold mt-1">Degustazione di {t.cliente_nome}</h1>
        <p className="text-sm text-[#47606B] mt-2">Passate il tablet agli sposi: per ogni piatto ✓ lo teniamo, ✗ lo togliamo, stelle e note per la cucina.</p>
      </div>
      {!t.dishes.length && <div className="rounded-xl bg-[#FEF3C7] p-4 text-sm" data-testid="tasting-empty">Il menù del contratto (Allegato A) è vuoto: compilatelo o caricatelo da foto/PDF, poi tornate qui.</div>}
      {secs.map((s) => <section key={s} className="space-y-2"><h2 className="text-xs uppercase tracking-[0.25em] text-[#0B6E8F]">{s}</h2>{t.dishes.map((d, i) => d.sezione === s && <Dish key={i} d={d} i={i} set={set(i)} />)}</section>)}
      <textarea rows={3} className={inputCls} placeholder="Note generali della degustazione" value={t.note} onChange={(e) => setT({ ...t, note: e.target.value })} data-testid="tasting-general-note" />
      <div className="sticky bottom-4 flex flex-wrap gap-2 justify-end bg-white/90 backdrop-blur rounded-full p-2 border border-[#D4E6EC] shadow-lg">
        <Button variant="outline" className="rounded-full" onClick={save} data-testid="tasting-save"><Save size={15} className="mr-1" /> Salva</Button>
        <Button className="rounded-full" onClick={apply} disabled={t.status !== "bozza"} title={t.status !== "bozza" ? "Contratto già in firma" : ""} data-testid="tasting-apply"><Wand2 size={15} className="mr-1" /> Aggiorna il menù del contratto</Button>
      </div>
    </div>
  );
}
